import React, { useState, useCallback } from 'react';
import OnboardingModal from '../onboarding_modal/OnboardingModal';
import { isJustSignedUp, getTourState, markTourComplete } from '../../lib/tour/tourApi';

const ROLE_SHOPPER = 'shopper';
const ROLE_FREELANCER = 'freelancer';
const ROLE_NONPROFIT = 'nonprofit';

/** SVG icons matching the Solvy design system (48x48, #eef8f5 bg, #2eb89a accent) */
const ShopIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="48" height="48">
    <rect width="48" height="48" rx="14" fill="#eef8f5" />
    <path d="M15 18L18 12H30L33 18" stroke="#2eb89a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="13" y="18" width="22" height="18" rx="2" fill="#1a3a2f" opacity="0.12" />
    <rect x="13" y="18" width="22" height="6" rx="2" fill="#2eb89a" />
    <circle cx="21" cy="30" r="2" fill="#2eb89a" opacity="0.6" />
    <circle cx="27" cy="30" r="2" fill="#2eb89a" opacity="0.6" />
    <path d="M21 32C21 33.5 22.3 35 24 35C25.7 35 27 33.5 27 32" stroke="#2eb89a" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const TalentIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="48" height="48">
    <rect width="48" height="48" rx="14" fill="#eef8f5" />
    <circle cx="24" cy="17" r="5" fill="#2eb89a" />
    <path d="M16 30C16 26 19.6 23 24 23C28.4 23 32 26 32 30" stroke="#1a3a2f" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
    <rect x="20" y="32" width="8" height="5" rx="1.5" fill="#2eb89a" opacity="0.5" />
    <path d="M24 32V37" stroke="#2eb89a" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M34 14L36 12M36 14L34 12" stroke="#2eb89a" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
    <circle cx="12" cy="24" r="1.5" fill="#2eb89a" opacity="0.4" />
  </svg>
);

const NonprofitIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="48" height="48">
    <rect width="48" height="48" rx="14" fill="#eef8f5" />
    <path d="M24 14C24 14 18 19 18 23C18 26.3 20.7 29 24 29C27.3 29 30 26.3 30 23C30 19 24 14 24 14Z" fill="#2eb89a" />
    <path d="M22 22.5L23.5 24L26.5 21" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="16" y="31" width="16" height="5" rx="2" fill="#1a3a2f" opacity="0.12" />
    <rect x="18" y="32" width="7" height="3" rx="1.5" fill="#2eb89a" opacity="0.6" />
    <circle cx="36" cy="16" r="2" fill="#2eb89a" opacity="0.3" />
    <circle cx="12" cy="32" r="1.5" fill="#2eb89a" opacity="0.4" />
  </svg>
);

/**
 * Role selection card used in Slide 3.
 */
const RoleCard = ({ icon, title, description, onClick }) => (
  <div
    onClick={onClick}
    className="tw-flex tw-items-center tw-gap-4 tw-border tw-border-solid tw-border-icons tw-rounded-xl tw-p-4 tw-cursor-pointer hover:tw-border-color1 hover:tw-shadow-md tw-transition-all"
  >
    <div className="tw-flex-shrink-0">{icon}</div>
    <div>
      <h4 className="tw-font-semibold tw-text-[16px] tw-leading-[20px] tw-text-main1 tw-mb-1">{title}</h4>
      <p className="tw-text-[13px] tw-leading-[150%] tw-text-main1 tw-font-normal tw-m-0">{description}</p>
    </div>
  </div>
);

/**
 * Returns the common slides (1-3) shown to all users.
 * Slide 3 includes interactive role selection cards.
 */
function getCommonSlides(onRoleSelect) {
  return [
    {
      title: 'Welcome to Solvy!',
      description: 'Solvy is a marketplace where every transaction funds a nonprofit project. Shopping and hiring talent always fund causes or projects you care about',
      content: (
        <div className="tw-text-center tw-py-4">
          <img
            src="https://expertpeople.imgix.net/assets/images/solvy-logo.svg"
            alt="Solvy"
            width="160"
            height="60"
            className="tw-w-40 tw-h-auto tw-mx-auto tw-mb-4"
          />
        </div>
      )
    },
    {
      title: 'How Solvy Works',
      description: 'Freelancers or Volunteers (Talent) offer Services, Classes & Experiences. Products are offered by Solvy or Brands. A significant portion of every purchase and hire funds a worthwhile cause or nonrprofit project',
      content: (
        <div className="tw-grid tw-grid-cols-3 tw-gap-3 tw-mt-4">
          <div className="tw-text-center tw-border tw-border-solid tw-border-icons tw-rounded-xl tw-p-4">
            <img src="https://expertpeople.imgix.net/assets/images/offer-illustration/service.svg" alt="Service" width="56" height="56" className="tw-w-14 tw-h-14 tw-mx-auto tw-mb-2" />
            <span className="tw-font-semibold tw-text-sm">Service</span>
          </div>
          <div className="tw-text-center tw-border tw-border-solid tw-border-icons tw-rounded-xl tw-p-4">
            <img src="https://expertpeople.imgix.net/assets/images/offer-illustration/class.svg" alt="Class" width="56" height="56" className="tw-w-14 tw-h-14 tw-mx-auto tw-mb-2" />
            <span className="tw-font-semibold tw-text-sm">Class</span>
          </div>
          <div className="tw-text-center tw-border tw-border-solid tw-border-icons tw-rounded-xl tw-p-4">
            <img src="https://expertpeople.imgix.net/assets/images/offer-illustration/product.svg" alt="Product" width="56" height="56" className="tw-w-14 tw-h-14 tw-mx-auto tw-mb-2" />
            <span className="tw-font-semibold tw-text-sm">Product</span>
          </div>
        </div>
      )
    },
    {
      title: 'What brings you to Solvy?',
      description: 'Choose your path — or explore on your own!',
      content: (
        <div className="tw-flex tw-flex-col tw-gap-3 tw-mt-4">
          <RoleCard
            icon={<ShopIcon />}
            title="Shop & Hire"
            description="Shop products, hire freelancers or volunteers (Talent) while funding causes you care about"
            onClick={() => onRoleSelect(ROLE_SHOPPER)}
          />
          <RoleCard
            icon={<TalentIcon />}
            title="Offer Your Talent"
            description="Freelance, Volunteer or sell your Digital Products and let your talent fund local nonprofit projects or causes"
            onClick={() => onRoleSelect(ROLE_FREELANCER)}
          />
          <RoleCard
            icon={<NonprofitIcon />}
            title="Fundraise for your Nonprofit"
            description="Add your nonprofit then create a project that needs funds. Solvy provides products and Talent. Raise more funds promoting Solvy to your supporters"
            onClick={() => onRoleSelect(ROLE_NONPROFIT)}
          />
        </div>
      )
    }
  ];
}

/**
 * Returns role-specific slide (Slide 4).
 */
function getRoleSlide(role) {
  switch (role) {
    case ROLE_SHOPPER:
      return {
        title: 'Shop With Purpose',
        description: 'At the checkout, choose a nonprofit project of your choice when you buy a Solvy product or hire a freelancer/volunteer supporting all Projects.',
        content: null
      };
    case ROLE_FREELANCER:
      return {
        title: 'Freelance or Volunteer',
        description: 'Choose the Causes or nonprofit Projects to support with your talent. Freelance or sell your Digital Products to Earn Money AND Do Good — Use the Solvy Slider to split revenue: keep up to 75%. Volunteer to contribute the maximum.',
        content: null
      };
    case ROLE_NONPROFIT:
      return {
        title: 'Showcase Your Mission & Fundraise',
        description: 'Add your nonprofit and create a project with a funding goal.',
        content: null
      };
    default:
      return null;
  }
}

function getFinishLabel(role) {
  switch (role) {
    case ROLE_SHOPPER: return 'Start Browsing';
    case ROLE_FREELANCER: return 'Learn More';
    case ROLE_NONPROFIT: return 'Learn More';
    default: return 'FINISH';
  }
}

function getRedirectPath(role) {
  switch (role) {
    case ROLE_SHOPPER: return '/products';
    case ROLE_FREELANCER: return '/freelancer';
    case ROLE_NONPROFIT: return '/nonprofit';
    default: return null;
  }
}

const WelcomeTour = () => {
  const shouldShow = isJustSignedUp() && !getTourState().welcome;
  const [isOpen, setIsOpen] = useState(shouldShow);
  const [selectedRole, setSelectedRole] = useState(null);

  const handleRoleSelect = useCallback((role) => {
    setSelectedRole(role);
    // Persist the chosen role so the role-specific auto-tours
    // (shopper / freelancer / nonprofit) know whether they should fire.
    try {
      localStorage.setItem('solvy_onboarding_role', role);
    } catch (e) {
      // localStorage may be unavailable (private mode); non-fatal.
    }
    // The slides will change, but we need to go to the next slide
    // We'll use a timeout to let React re-render, then advance
    setTimeout(() => {
      // Find the slider and go to the last slide (index 3 = role-specific)
      const nextBtn = document.querySelector('.onboarding-arrow-outside.next');
      if (nextBtn) nextBtn.click();
    }, 100);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    markTourComplete('welcome');
    // Remove the meta tag to prevent re-showing on same page load
    const meta = document.querySelector('meta[name="just-signed-up"]');
    if (meta) meta.remove();
  }, []);

  const handleFinish = useCallback(async () => {
    setIsOpen(false);
    // Wait for the API call to complete before redirecting,
    // so the next page load has the updated tour_state in the meta tag
    await markTourComplete('welcome');
    // Remove the meta tag
    const meta = document.querySelector('meta[name="just-signed-up"]');
    if (meta) meta.remove();
    // Redirect based on selected role
    const redirectPath = getRedirectPath(selectedRole);
    if (redirectPath) {
      window.location.href = redirectPath;
    }
  }, [selectedRole]);

  if (!isOpen) return null;

  // Build slides: 3 common + optional role-specific
  const commonSlides = getCommonSlides(handleRoleSelect);
  const slides = [...commonSlides];

  if (selectedRole) {
    const roleSlide = getRoleSlide(selectedRole);
    if (roleSlide) {
      slides.push(roleSlide);
    }
  }

  return (
    <OnboardingModal
      opened={isOpen}
      closeAction={handleClose}
      slides={slides}
      skipLabel="SKIP"
      nextLabel="NEXT"
      finishLabel={selectedRole ? getFinishLabel(selectedRole) : 'FINISH'}
      onFinish={handleFinish}
    />
  );
};

export default WelcomeTour;
